import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

export default function TermsOfService() {
  const navigate = useNavigate();
  const lastUpdated = "February 14, 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-4 py-4 bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg object-cover" />
            <span className="font-display text-xl font-bold text-gray-900">TalentMeet</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-gray-500 mb-8">Last updated: {lastUpdated}</p>

            <div className="prose prose-gray max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-600 mb-4">
                  By accessing or using TalentMeet ("the Platform"), you agree to be bound by these Terms of Service 
                  ("Terms"). If you do not agree to these Terms, please do not use our Platform.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
                <p className="text-gray-600 mb-4">
                  TalentMeet is an online platform that connects entertainment industry professionals including:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li><strong>Talent:</strong> Artists, actors, models, singers, dancers, and other performers</li>
                  <li><strong>Trainers:</strong> Coaches, instructors, and mentors in the entertainment field</li>
                  <li><strong>Enterprises:</strong> Production houses, talent agencies, casting directors, and studios</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
                <p className="text-gray-600 mb-4">To use certain features, you must create an account. You agree to:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain the security of your password and account</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized use</li>
                  <li>Be at least 18 years of age</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">4. User Content</h2>
                <p className="text-gray-600 mb-4">
                  You retain ownership of content you post on TalentMeet. By posting content, you grant us a 
                  non-exclusive, worldwide, royalty-free license to use, display, and distribute your content 
                  in connection with the Platform.
                </p>
                <p className="text-gray-600 mb-4">You agree not to post content that:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li>Infringes on intellectual property rights</li>
                  <li>Contains false, misleading, or deceptive information</li>
                  <li>Is defamatory, obscene, or offensive</li>
                  <li>Violates any applicable laws or regulations</li>
                  <li>Contains viruses or malicious code</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Prohibited Activities</h2>
                <p className="text-gray-600 mb-4">You agree not to:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li>Use the Platform for any illegal purpose</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Impersonate any person or entity</li>
                  <li>Interfere with or disrupt the Platform</li>
                  <li>Attempt to gain unauthorized access to any systems</li>
                  <li>Collect user information without consent</li>
                  <li>Use automated systems to access the Platform</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Subscriptions and Payments</h2>
                <p className="text-gray-600 mb-4">
                  Certain features require a paid subscription. Subscription terms:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li>Subscriptions auto-renew unless cancelled</li>
                  <li>Refunds are subject to our refund policy</li>
                  <li>Prices may change with notice</li>
                  <li>Free trials convert to paid subscriptions automatically</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Platform Connections</h2>
                <p className="text-gray-600 mb-4">
                  TalentMeet facilitates connections but is not responsible for:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li>The accuracy of user profiles or claims</li>
                  <li>The outcome of any business arrangements</li>
                  <li>Disputes between users</li>
                  <li>Quality of services provided by trainers or enterprises</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Intellectual Property</h2>
                <p className="text-gray-600 mb-4">
                  The TalentMeet name, logo, and all related marks are our trademarks. The Platform's design, 
                  features, and content are protected by intellectual property laws.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Disclaimer of Warranties</h2>
                <p className="text-gray-600 mb-4">
                  THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THE 
                  ACCURACY, COMPLETENESS, OR USEFULNESS OF ANY INFORMATION ON THE PLATFORM.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
                <p className="text-gray-600 mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, TALENTMEET SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                  INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE PLATFORM.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Termination</h2>
                <p className="text-gray-600 mb-4">
                  We may terminate or suspend your account at any time for violations of these Terms. 
                  You may delete your account at any time through account settings.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
                <p className="text-gray-600 mb-4">
                  We reserve the right to modify these Terms at any time. We will notify users of significant 
                  changes. Continued use of the Platform after changes constitutes acceptance.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
                <p className="text-gray-600 mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of India, 
                  without regard to its conflict of law provisions.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
                <p className="text-gray-600">
                  For questions about these Terms, please contact us at:
                </p>
                <p className="text-violet-600 font-medium mt-2">
                  <a href="mailto:support@talentmeet.in">support@talentmeet.in</a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm">&copy; 2026 TalentMeet. All rights reserved.</p>
          <div className="flex gap-6 text-sm">
            <button onClick={() => navigate("/about")} className="hover:text-white transition-colors">About</button>
            <button onClick={() => navigate("/terms")} className="hover:text-white transition-colors">Terms</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
